import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FeaturedProducts = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/products`).then(res => setProducts(res.data)).catch(() => {});
  }, []);

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: (i) => ({
      opacity: 1, y: 0,
      transition: { duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
    }),
  };

  return (
    <section id="products" data-testid="products-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.p
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0}
          className="text-[#C5A55A] text-sm font-mono tracking-[0.3em] uppercase mb-4"
        >
          {t('products.overline')}
        </motion.p>
        <motion.h2
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1}
          className="font-heading text-4xl md:text-5xl font-semibold tracking-tight text-[#0A1628] mb-4"
          data-testid="products-title"
        >
          {t('products.title')}
        </motion.h2>
        <motion.p
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2}
          className="text-slate-600 text-base md:text-lg leading-relaxed mb-6 max-w-2xl"
        >
          {t('products.subtitle')}
        </motion.p>

        <motion.div
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2.5}
          className="mb-16"
        >
          <a
            href="https://medecinesupply.com/wp-content/uploads/2023/07/product_catalogue3.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#0A1628] text-white font-semibold px-6 py-3 hover:bg-[#123052] transition-all duration-300 tracking-wider text-sm uppercase"
            data-testid="products-catalog-pdf-link"
          >
            <CheckCircle className="w-4 h-4 text-[#C5A55A]" />
            {t('products.catalogPdf')}
          </a>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.map((product, i) => (
            <motion.div
              key={product.id}
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i + 3}
              className="group bg-white border border-slate-100 shadow-sm hover:shadow-xl hover:border-[#C5A55A] transition-all duration-500 cursor-pointer"
              data-testid={`product-card-${product.id}`}
              onClick={() => navigate(`/product/${product.id}`)}
            >
              <div className="relative overflow-hidden h-48">
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                {product.in_stock && (
                  <div className="absolute top-3 right-3 flex items-center gap-1 bg-emerald-500 text-white text-xs font-semibold px-3 py-1">
                    <CheckCircle className="w-3 h-3" />
                    {t('products.inStock')}
                  </div>
                )}
                <div className="absolute top-3 left-3 bg-[#0A1628]/80 text-[#C5A55A] text-xs font-mono px-3 py-1 tracking-wider uppercase">
                  {product.category}
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-heading text-lg font-semibold text-[#0A1628] mb-2 group-hover:text-[#C5A55A] transition-colors">
                  {product.name}
                </h3>
                <p className="text-slate-500 text-sm leading-relaxed mb-4 line-clamp-2">
                  {product.description}
                </p>
                <div className="flex items-center justify-between">
                  <p className="text-[#0A1628] font-semibold text-sm">{product.price_range}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedProducts;
